import { z } from "zod";
import { extendZodWithOpenApi } from "zod-openapi";
import { ind, jsonBody, successResponse, tagPaths, unref } from "../util.ts";
import { simpleSuccessResponse, storage_pool_uuid, uuid } from "./common.ts";
import * as params from "./params.ts";
import { GuestLimits, HostPool } from "./pools.ts";

extendZodWithOpenApi(z);

//#region Admin Host Pool

export const AdminHostPool = HostPool.extend({
    cpu_allocate_coefficient: z
        .number()
        .describe("Multiplier applied to host CPU threads to calculate VM vCPU capacity in this pool"),
    cpu_model: z.string().describe("Libvirt CPU model that hosts in this pool must support"),
    hosts_assigned_count: z.number().describe("Number of hypervisors currently assigned to this pool"),
    is_hv_auto_assigned: z.boolean().describe("Whether newly added hosts are automatically assigned to this pool"),
    is_visible: z.boolean().describe("Whether the pool is shown to end users"),
    mem_allocate_coefficient: z
        .number()
        .describe("Multiplier applied to host memory to calculate VM RAM capacity in this pool"),
    os_type: z.string().describe("Guest OS family supported by this pool, such as `multi`, `linux`, or `windows`"),
}).openapi({ ref: "AdminHostPool" });

export const AdminHostPoolCreateBody = z
    .object({
        name: z.string().describe("Admin-visible name of the host pool"),
        description: z.string().optional().describe("Optional free-form note for operators"),
        cpu_model: AdminHostPool.shape.cpu_model,
        cpu_allocate_coefficient: AdminHostPool.shape.cpu_allocate_coefficient,
        mem_allocate_coefficient: AdminHostPool.shape.mem_allocate_coefficient,
        ui_position: z.number().optional().describe("Optional ordering index used in UI lists"),
        storage_pool_uuid: unref(storage_pool_uuid).describe("Default block storage pool for VMs created in this pool"),
        guest_limits: unref(GuestLimits).optional().describe("Optional per-VM CPU, RAM, and disk size bounds"),
    })
    .openapi({ ref: "AdminHostPoolCreateBody" });

export const AdminHostPoolUpdateBody = z
    .object({
        name: AdminHostPoolCreateBody.shape.name,
        description: AdminHostPoolCreateBody.shape.description,
        cpu_model: AdminHostPoolCreateBody.shape.cpu_model,
        cpu_allocate_coefficient: AdminHostPoolCreateBody.shape.cpu_allocate_coefficient,
        mem_allocate_coefficient: AdminHostPoolCreateBody.shape.mem_allocate_coefficient,
        ui_position: z.number().describe("Ordering index used in UI lists"),
        uuid,
        storage_pool_uuid: AdminHostPoolCreateBody.shape.storage_pool_uuid,
        is_visible: z.boolean().optional().describe("Whether the pool should remain visible to end users"),
        guest_limits: AdminHostPoolCreateBody.shape.guest_limits,
    })
    .openapi({ ref: "AdminHostPoolUpdateBody" });
//#endregion

export const AdminHostPoolResponse = z.array(AdminHostPool);

export const hostPoolAdminPaths = tagPaths("admin_host_pools")({
    // Admin list
    "/{location}/base-operator/admin/host_pool/list": {
        get: {
            summary: "List Host Pools",
            parameters: [params.location],
            responses: {
                ...successResponse(AdminHostPoolResponse),
            },
        },
    },
    "/{location}/base-operator/admin/host_pool": {
        post: {
            summary: "Create host pool",
            description:
                "Creates a host pool. Allocation coefficients define the host capacity exposed to VMs, and guest limits optionally constrain individual VM sizes within the pool.",
            parameters: [params.location],
            requestBody: jsonBody(AdminHostPoolCreateBody),
            responses: {
                ...successResponse(AdminHostPool),
            },
        },
    },
    "/{location}/base-operator/admin/host_pool/{uuid}": {
        patch: {
            summary: "Update host pool",
            parameters: [params.location, params.uuid],
            requestBody: jsonBody(AdminHostPoolUpdateBody),
            responses: {
                ...successResponse(AdminHostPool),
            },
        },
        delete: {
            summary: "Delete host pool",
            parameters: [params.location, params.uuid],
            responses: { ...simpleSuccessResponse },
        },
    },
    "/{location}/base-operator/admin/host_pool/{uuid}/default_for_vms": {
        put: {
            summary: "Set host pool as default for VMs",
            description: ind`
                Sets \`is_default_designated\` on the specified host pool and clears it from the cluster's current default designated pool.

                This pool becomes the preselected designated pool for new VMs unless another pool is specified explicitly.

                Exactly one host pool must hold this flag within a cluster.`,
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(AdminHostPool),
            },
        },
    },
    "/{location}/base-operator/admin/host_pool/{uuid}/default_for_hosts": {
        put: {
            summary: "Set host pool as default for new hosts",
            description: ind`
                Sets \`is_hv_auto_assigned\` on the specified host pool and clears it from any other pool in the cluster.

                Newly added hosts are assigned to this pool automatically.

                Exactly one host pool must hold this flag within a cluster.`,
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(AdminHostPool),
            },
        },
    },
});
